import React, { useRef, forwardRef } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import moment from "moment";
import { CalenderIcon } from "../../../assets/CommonSvg";

const parseDate = (val, fmt) => {
  if (!val) return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : moment(val);
  }
  let m = moment(val, fmt, true);
  if (m.isValid()) return m;
  m = moment(val);
  if (m.isValid()) return m;
  m = moment(val, fmt);
  if (m.isValid()) return m;
  return null;
};

const CustomInput = forwardRef(({ format, value, onClick, label, placeholder, disabled, className }, ref) => (
  <div className={`relative w-full ${label ? ' mt-6' : ' mt-6'} mb-2.5`}>
    <input
      style={{
        ...(disabled && { backgroundColor: "#F0F1F5", color: "#5c5959" }),
        border: "1px solid",
        padding: "13px"
      }}

      className={`date-picker-custom-class block w-full h-full  !border-[#F0F1F5] bg-[#FFFFFF] rounded-[6px]   outline-none  ` + className}
      onClick={onClick}
      value={(() => {
        const m = parseDate(value, format || "DD/MM/YYYY");
        return m ? m.format(format || "DD/MM/YYYY") : "";
      })()}
      placeholder={placeholder}
      readOnly
      ref={ref}
      disabled={disabled}
    />
    {!disabled &&
      <span
        className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
        onClick={onClick}
      >
        <CalenderIcon />
      </span>}
  </div>
));

const DatePickerField = ({
  className,
  handleChange,
  value,
  InputId,
  InputName,
  format,
  placeholder,
  disabled,
  minDate = false,
  maxDate,
  ...props
}) => {
  const datePickerRef = useRef(null);
  const handleDateSelect = (date, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    handleChange(moment(date).format(format || "DD/MM/YYYY"));
  };
  // Convert moment format tokens to date-fns tokens (used by react-datepicker internally).
  // This ensures react-datepicker passes its formatted value to CustomInput in the correct
  // format (DD/MM/YYYY) instead of the US default (MM/DD/YYYY), preventing display corruption
  // when the user picks a date from the next month.
  const toDateFnsFormat = (momentFmt) => {
    return (momentFmt || "DD/MM/YYYY")
      .replace(/DD/g, "dd")
      .replace(/YYYY/g, "yyyy");
  };
  const datePickerFormat = toDateFnsFormat(format);

  return (
    <div className="relative w-full">
      <DatePicker
        ref={datePickerRef}
        selected={(() => {
          const m = parseDate(value, format || "DD/MM/YYYY");
          return m ? m.toDate() : null;
        })()}
        onChange={handleDateSelect}
        dateFormat={datePickerFormat}
        id={InputId}
        name={InputName}
        placeholderText={placeholder}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        minDate={(() => {
          if (!minDate) return null;
          if (typeof minDate === "boolean") {
            return minDate ? new Date() : null;
          }
          if (minDate instanceof Date) {
            return isNaN(minDate.getTime()) ? null : minDate;
          }
          const m = moment(minDate);
          return m.isValid() ? m.toDate() : null;
        })()}
        maxDate={(() => {
          if (!maxDate) return null;
          if (maxDate instanceof Date) {
            return isNaN(maxDate.getTime()) ? null : maxDate;
          }
          const m = moment(maxDate);
          return m.isValid() ? m.toDate() : null;
        })()}
        disabled={disabled}
        onFocus={(event) => event.target.setAttribute("autocomplete", "off")}
        customInput={<CustomInput value={value} placeholder={placeholder} disabled={disabled} className={className} format={format} />}
        wrapperClassName="block w-full"
        popperClassName="custom-datepicker"
        {...props}
      />
    </div>
  );
};

DatePickerField.propTypes = {
  handleChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  InputId: PropTypes.string,
  InputName: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

export default DatePickerField;
